import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { UsersClient, ApplicationUserDto, CreateSystemUserCommand, UpdateSystemUserRequest } from '../../web-api-client';
import { createApiClient } from '../../utils/apiClientFactory';
import { Roles } from '../../constants/roles';
import { getErrorMessage } from '../../utils/errorHelpers';

interface UserModalProps {
  user: ApplicationUserDto | null;
  onClose: (refresh: boolean) => void;
}

const AVAILABLE_ROLES = [Roles.Administrator];

export default function UserModal({ user, onClose }: UserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    roles: [] as string[]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        fullName: user.fullName || '',
        roles: user.roles || []
      });
    }
  }, [user]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Roles are optional - users without roles are regular users

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiErrors([]);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const client = createApiClient(UsersClient);

      if (user) {
        // Update existing user
        const updateRequest = new UpdateSystemUserRequest({
          email: formData.email,
          fullName: formData.fullName || undefined,
          roles: formData.roles
        });
        await client.updateSystemUser(user.id!, updateRequest);
        toast.success('User updated successfully!');
      } else {
        // Create new user (email is used as username for login)
        // Password is auto-generated by the backend (SSO will be used)
        const createCommand = new CreateSystemUserCommand({
          email: formData.email,
          fullName: formData.fullName || undefined,
          roles: formData.roles
        });
        await client.createSystemUser(createCommand);
        toast.success('User created successfully!');
      }

      onClose(true);
    } catch (error: unknown) {
      console.error('Failed to save user:', error);

      if (error && typeof error === 'object' && 'response' in error && (error as { response: unknown }).response) {
        try {
          const responseStr = (error as { response: string }).response;
          const result = JSON.parse(responseStr);
          if (result.errors && Array.isArray(result.errors)) {
            setApiErrors(result.errors);
            toast.error(result.errors[0] || 'Failed to save user');
          } else {
            setApiErrors(['An error occurred while saving the user']);
            toast.error('An error occurred while saving the user');
          }
        } catch {
          setApiErrors(['An error occurred while saving the user']);
          toast.error('An error occurred while saving the user');
        }
      } else {
        setApiErrors(['An error occurred while saving the user']);
        toast.error('An error occurred while saving the user');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (role: string) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={() => onClose(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Header */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white p-6 pb-0">
          {user ? 'Edit User' : 'Add New User'}
        </h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {apiErrors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                {apiErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-5">
            {/* Email (used as login username) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 ${
                  errors.email ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="user@example.com"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This email will be used as the login username
              </p>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter full name"
              />
            </div>

            {/* Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Privileges
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Check this box to grant administrator access. Leave unchecked for regular user.
              </p>
              <div className="space-y-3">
                {AVAILABLE_ROLES.map((role) => (
                  <label
                    key={role}
                    className="flex items-center p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150"
                  >
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role)}
                      onChange={() => toggleRole(role)}
                      className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">{role}</span>
                  </label>
                ))}
              </div>
              {errors.roles && (
                <p className="mt-2 text-sm text-red-600">{errors.roles}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="inline-block h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {user ? 'Updating...' : 'Creating...'}
                </span>
              ) : (
                <span>{user ? 'Update User' : 'Create User'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
